import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';

/**
 * BrowserCompatibilityNotice — Iter 191 (Feb 2026)
 *
 * Some Android browsers won't install Find-2-Day as a real PWA — they create
 * a bookmark with the hostname as the label instead. There's no fix on our
 * side for those browsers, so we detect them and surface a one-line nudge
 * pointing the user at Chrome / Samsung Internet / Edge.
 *
 * Detection covers:
 *   - DuckDuckGo browser  (UA string: "DuckDuckGo/")
 *   - Firefox Focus / Klar (UA string: "Focus/" or "Klar/" — NOT plain Firefox)
 *   - Brave                (hidden from UA; uses navigator.brave.isBrave())
 *
 * Suppressed when:
 *   - The app is already running standalone (so the user is in the proper
 *     installed app — nothing to warn about).
 *   - The user previously dismissed the notice (stored in localStorage so
 *     they don't see it on every visit).
 *   - Desktop browsers (this is an Android install-experience issue).
 */

const DISMISS_KEY = 'find2day_browser_compat_dismiss';

const detectLimitedBrowser = async () => {
  if (typeof window === 'undefined' || !window.navigator) return null;
  const ua = navigator.userAgent || '';
  const isMobile = /Android|iPhone|iPad|iPod/.test(ua);
  if (!isMobile) return null;

  const isStandalone =
    window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
  if (isStandalone) return null;

  if (/DuckDuckGo\//i.test(ua)) return 'duckduckgo';
  if (/Focus\/|Klar\//.test(ua)) return 'firefox-focus';

  // Brave hides from the UA, so feature-detect via navigator.brave.isBrave().
  // The promise resolves to `true` on Brave only.
  try {
    if (navigator.brave && typeof navigator.brave.isBrave === 'function') {
      const isBrave = await navigator.brave.isBrave();
      if (isBrave) return 'brave';
    }
  } catch {
    // ignore — Brave detection is best-effort
  }
  return null;
};

const BrowserCompatibilityNotice = () => {
  const { t } = useTranslation();
  const [browserKind, setBrowserKind] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY) === '1') {
      setDismissed(true);
      return;
    }
    let cancelled = false;
    detectLimitedBrowser().then((kind) => {
      if (!cancelled) setBrowserKind(kind);
    });
    return () => { cancelled = true; };
  }, []);

  if (dismissed || !browserKind) return null;

  const browserLabel = {
    duckduckgo: 'DuckDuckGo',
    'firefox-focus': 'Firefox Focus',
    brave: 'Brave',
  }[browserKind];

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      role="status"
      data-testid="browser-compat-notice"
      data-browser={browserKind}
      className="w-full max-w-2xl mx-auto mb-3 sm:mb-4 px-3"
    >
      <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 shadow-sm">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-0 text-sm leading-snug text-amber-900">
          <p className="font-semibold mb-0.5">
            {t('browserCompat.title', { browser: browserLabel, defaultValue: `You're using {{browser}}` })}
          </p>
          <p className="text-amber-800">
            {t('browserCompat.body', `For the full Find-2-Day app — proper icon, no website address as the name, full-screen launch — please open this page in Chrome, Samsung Internet or Edge.`)}
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="shrink-0 -mt-1 -mr-1 p-1 rounded-md text-amber-700 hover:bg-amber-100 transition-colors"
          aria-label={t('browserCompat.dismissAria', 'Dismiss browser compatibility notice')}
          data-testid="browser-compat-dismiss"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default BrowserCompatibilityNotice;
